// Per-visit session identity for the widget.
//
// A random UUID kept in sessionStorage so every chat turn and every
// conversion event from one visit share a correlation id — that's what lets
// the API tie a conversion back to the chat (and the RAG searches) that drove
// it. Per-VISIT, not per-visitor: sessionStorage clears when the tab closes,
// so there's no cross-visit tracking and the id is not PII.

const KEY = "aiw_sid";
// Fallback when sessionStorage is unavailable (Safari private mode, storage
// disabled) — keeps the session correlated within a single page load.
let memorySid: string | null = null;

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the manual generator
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = memorySid ?? randomId();
    sessionStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    if (!memorySid) memorySid = randomId();
    return memorySid;
  }
}

// Tag an outbound link with the session id (and an optional ref, e.g. a plan
// slug) so a downstream conversion on the destination page can be attributed
// back to this chat. The host-page attribution shim reads ?aiw_sid and drops
// a cookie that a later contact-form/brochure submit resolves. Only http(s)
// URLs are tagged; anything unparseable is returned untouched.
export function appendAttribution(url: string, ref?: string): string {
  try {
    const u = new URL(url, location.href);
    if (u.protocol !== "http:" && u.protocol !== "https:") return url;
    u.searchParams.set("aiw_sid", getSessionId());
    if (ref) u.searchParams.set("aiw_ref", ref);
    return u.toString();
  } catch {
    return url;
  }
}

// --- Conversion event tracking -------------------------------------------
//
// App registers the active widget's API context here so click handlers deep
// in the component tree (Card, Sources) — and the window.AIWidget.track host
// bridge — can fire events without prop-drilling. Last-configured wins, which
// is correct for the overwhelmingly common single-widget-per-page case.

import { sendEvent } from "./api";

type EventCtx = { apiUrl: string; clientId: string; gaPush: boolean };
let eventCtx: EventCtx | null = null;

export function configureEvents(ctx: EventCtx): void {
  eventCtx = ctx;
}

// Push a conversion event into the host page's analytics. Two stacks exist in
// the wild and need different calls:
//   - plain gtag.js (e.g. Google Site Kit): a custom {event: ...} dataLayer
//     push is inert — GA4 only records gtag('event', name, params). Event name
//     is "aiw_" + type (all current types stay under GA4's 40-char limit).
//   - GTM container: gtag() may not exist; push the aiw_conversion object and
//     let a GTM trigger map it.
// Exactly one path runs (gtag() itself feeds dataLayer, so calling both would
// double-count under GTM). Gated by the per-client gaPushEnabled flag
// (surfaced via /v1/config) so we don't double-count for clients who listen to
// the DOM CustomEvent instead.
function pushAnalytics(
  type: string,
  ref: string | undefined,
  metadata: Record<string, unknown> | undefined,
  sid: string,
): void {
  try {
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: unknown[];
    };
    if (typeof w.gtag === "function") {
      w.gtag("event", "aiw_" + type, {
        aiw_session_id: sid,
        aiw_ref: ref ?? null,
        ...(metadata ?? {}),
      });
      return;
    }
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "aiw_conversion",
      aiw_type: type,
      aiw_session_id: sid,
      aiw_ref: ref ?? null,
      ...(metadata ?? {}),
    });
  } catch {
    // no window / no analytics — ignore
  }
}

export function track(
  type: string,
  ref?: string,
  metadata?: Record<string, unknown>,
): void {
  if (!eventCtx) return;
  const sid = getSessionId();
  void sendEvent(eventCtx.apiUrl, eventCtx.clientId, sid, type, ref, metadata);
  if (eventCtx.gaPush) pushAnalytics(type, ref, metadata, sid);
}

// Host-page bridge: window.AIWidget.track(type, metadata) routes here.
// Records the event with source='bridge' AND dispatches a DOM CustomEvent so
// the host's own analytics (GTM/GA listeners) can pick it up. Requires a
// mounted widget on this page (eventCtx set); cross-page/new-tab conversions
// are handled by the attribution shim reading the aiw_sid cookie instead.
export function trackHost(
  type: string,
  metadata?: Record<string, unknown>,
): void {
  const sid = getSessionId();
  if (eventCtx) {
    void sendEvent(
      eventCtx.apiUrl,
      eventCtx.clientId,
      sid,
      type,
      undefined,
      metadata,
      "bridge",
    );
  }
  try {
    window.dispatchEvent(
      new CustomEvent("aiwidget:conversion", {
        detail: { type, metadata: metadata ?? {}, sessionId: sid },
      }),
    );
  } catch {
    // no window / SSR — ignore
  }
  if (eventCtx?.gaPush) pushAnalytics(type, undefined, metadata, sid);
}
